<script setup lang="ts">
import { SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { ref, watch } from "vue"

import InlineEmpty from "@/components/InlineEmpty.vue"
import { getJson, postJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import { itemLinksPath, searchItemLinksPath } from "@/routes"
import type { ItemSearchResult } from "@/types/models"

const props = defineProps<{
  open: boolean
  itemId: string
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
  linked: []
}>()

const query = ref("")
const results = ref<ItemSearchResult[]>([])
const searching = ref(false)
const linking = ref<string | null>(null)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => props.open,
  (open) => {
    if (open) {
      query.value = ""
      results.value = []
    }
  },
)

watch(query, (q) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (!q.trim()) {
    results.value = []
    return
  }
  debounceTimer = setTimeout(() => search(q.trim()), 300)
})

async function search(q: string) {
  searching.value = true
  const res = await getJson(`${searchItemLinksPath(props.itemId)}?q=${encodeURIComponent(q)}`)
  if (res.ok) {
    results.value = await res.json()
  }
  searching.value = false
}

async function linkItem(targetId: string) {
  linking.value = targetId
  const res = await postJson(itemLinksPath(props.itemId), { target_item_id: targetId })
  if (res.ok) {
    results.value = results.value.filter((r) => r.id !== targetId)
    emit("linked")
  }
  linking.value = null
}
</script>

<template>
  <USlideover :open="open" title="Link Items" @update:open="emit('update:open', $event)">
    <template #body>
      <div class="flex flex-col gap-4">
        <UInput
          v-model="query"
          :icon="ICON.search"
          placeholder="Search items to link..."
          autofocus
          class="w-full"
        />

        <div v-if="searching" class="flex justify-center py-8">
          <UIcon name="i-lucide-loader-2" class="text-muted size-6 animate-spin" />
        </div>

        <div v-else-if="results.length" class="flex flex-col">
          <SbListItem v-for="item in results" :key="item.id">
            <SbListSection avatar>
              <UAvatar
                :src="item.thumb_url ?? undefined"
                :alt="item.name"
                size="sm"
                :ui="{ image: 'object-cover' }"
              />
            </SbListSection>
            <SbListSection>
              <SbListLabel :lines="1">{{ item.name }}</SbListLabel>
              <SbListLabel v-if="item.location_path" caption>{{ item.location_path }}</SbListLabel>
            </SbListSection>
            <SbListSection side>
              <UButton
                label="Link"
                size="xs"
                color="neutral"
                variant="subtle"
                :loading="linking === item.id"
                @click="linkItem(item.id)"
              />
            </SbListSection>
          </SbListItem>
        </div>

        <InlineEmpty
          v-else-if="query.trim() && !searching"
          :icon="ICON.search"
          message="No items found"
        />

        <InlineEmpty v-else :icon="ICON.link" message="Search for items to create a link" />
      </div>
    </template>
  </USlideover>
</template>
